import { ActivityIndicator, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { isOfflineCode, loadErrorText } from "../loadErrorText";
import { InlineStatusHint } from "../primitives/InlineStatusHint";
import type { UiTheme } from "../theme";
import { makeTierOneStyles } from "./panelStyles";
import { WidgetTitleRow } from "./InfoPill";

// Shared loading / error cards for the Tier-1 panels (lifted from mobile's
// TierOnePanels). `titleKey` is the short panel id (balance/pain/trends);
// the card renders `mobile.progress.<id>.title` in the WidgetTitleRow, which
// carries the collapse ▲ of an open Progress widget (PG-57).
export function LoadingCard({
  theme,
  titleKey,
}: {
  theme: UiTheme;
  titleKey: string;
}) {
  const styles = makeTierOneStyles(theme);
  return (
    <View style={styles.card} accessibilityLiveRegion="polite">
      <CardTitle theme={theme} titleKey={titleKey} />
      <ActivityIndicator color={theme.palette.brand500} />
    </View>
  );
}

export function ErrorCard({
  theme,
  titleKey,
  error,
}: {
  theme: UiTheme;
  titleKey: string;
  /** The code the read failed with: offline says so, anything else says it failed. */
  error: string;
}) {
  const styles = makeTierOneStyles(theme);
  const offline = isOfflineCode(error);
  return (
    <View
      style={[styles.card, offline ? null : styles.cardError]}
      accessibilityRole={offline ? undefined : "alert"}
    >
      <CardTitle theme={theme} titleKey={titleKey} />
      <LoadErrorLine theme={theme} error={error} />
    </View>
  );
}

/** What a failed read with nothing to show says. Offline is neutral (the
 *  `cached` hint: a glyph, ink text, polite); a real fault stays red, and its
 *  card says `alert`. */
export function LoadErrorLine({ theme, error }: { theme: UiTheme; error: string }) {
  const { t } = useTranslation();
  const styles = makeTierOneStyles(theme);
  return isOfflineCode(error) ? (
    <InlineStatusHint theme={theme} tone="cached" text={loadErrorText(t, error)} />
  ) : (
    <Text style={styles.errorText}>{loadErrorText(t, error)}</Text>
  );
}

/** A panel with nothing to draw yet (#397): its read failed, or still runs.
 *  Data kept from an earlier read never comes here: it draws, with StaleHint. */
export function NoDataCard({
  theme,
  titleKey,
  error,
}: {
  theme: UiTheme;
  titleKey: string;
  /** The failure code, or null while the read still runs. */
  error: string | null;
}) {
  return error ? (
    <ErrorCard theme={theme} titleKey={titleKey} error={error} />
  ) : (
    <LoadingCard theme={theme} titleKey={titleKey} />
  );
}

/** Numbers on screen the network has not confirmed (PG-15, #397): kept, and
 *  said so once above them. `refreshing`: a read is still running; else the
 *  last one failed. The coverage card and the panels say it the same way. */
export function StaleHint({
  theme,
  refreshing = false,
}: {
  theme: UiTheme;
  refreshing?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <InlineStatusHint
      theme={theme}
      tone={refreshing ? "refreshing" : "error"}
      text={
        refreshing
          ? t("mobile.progress.coverageRefreshing", { defaultValue: "Updating…" })
          : t("mobile.progress.coverageNotConfirmed")
      }
    />
  );
}

function CardTitle({ theme, titleKey }: { theme: UiTheme; titleKey: string }) {
  const { t } = useTranslation();
  return (
    <WidgetTitleRow
      theme={theme}
      title={t(`mobile.progress.${titleKey}.title`, { defaultValue: titleKey })}
    />
  );
}
